import { useState, useRef, useMemo, useCallback } from 'react';
import {
  DestinationTypeEnum,
  type Destination,
  type CreateDestinationPayload,
  type UpdateDestinationPayload,
} from '@/types/greenroom/destination.types';
import {
  GetDestinations,
  CreateDestination,
  UpdateDestination,
  DeleteDestination,
} from '@/connection/greenroom/destinations';
import { DESTINATION_TYPE_OPTIONS, getDefaultDestinationOption } from './destination-type-options';
import { dedupeDestinationsByCredentials } from './dedupe-destinations';
import type { ChannelType } from '@/types/channel.types';
import deepEqual from 'fast-deep-equal';

export type LocalDestination = {
  localId: string;
  id?: number;
  isNew?: boolean;
  type: DestinationTypeEnum;
  name: string;
  url: string;
  streamKey: string;
  selected: boolean;
};

export const useDestinationManager = (roomId: string | undefined, onClose: () => void, channelType?: ChannelType) => {
  const [destinations, setDestinations] = useState<LocalDestination[]>([]);
  const [pendingDeleteIds, setPendingDeleteIds] = useState<number[]>([]);
  const [touchedUrlIds, setTouchedUrlIds] = useState<Set<string>>(new Set());
  const savedDestinationsRef = useRef<LocalDestination[]>([]);

  const loadDestinations = useCallback(
    async (id: string): Promise<LocalDestination[] | null> => {
      const response = await GetDestinations(id);
      if (!response?.destinations) return null;

      const mapped: LocalDestination[] = response.destinations.map((d: Destination) => {
        // The server may return a synthetic BLP/platform destination with no `type` —
        // normalize it to Streams and source its label from the channel's default option.
        // See destination.types.ts for the full contract.
        const type = d.type ?? DestinationTypeEnum.Streams;
        const name =
          d.name ??
          (type === DestinationTypeEnum.Streams
            ? getDefaultDestinationOption(channelType).label
            : (DESTINATION_TYPE_OPTIONS.find((o) => o.value === type)?.label ?? ''));
        return {
          localId: d.id ? String(d.id) : d.url || String(d.type || Date.now()),
          id: d.id,
          type,
          name,
          url: d.url ?? '',
          streamKey: d.streamKey ?? '',
          selected: !!d.selected,
        };
      });

      const deduped = dedupeDestinationsByCredentials(mapped);

      savedDestinationsRef.current = structuredClone(deduped);
      setDestinations(deduped);
      setTouchedUrlIds(new Set());
      return deduped;
    },
    [channelType]
  );

  const hasChanges = useMemo(() => {
    if (pendingDeleteIds.length > 0) return true;
    return !deepEqual(destinations, savedDestinationsRef.current);
  }, [destinations, pendingDeleteIds]);

  const validation = useMemo(() => {
    const hasEmptyNames = destinations.some((d) => d.type === DestinationTypeEnum.Other && !d.name.trim());
    const hasInvalidUrls = destinations.some((d) => {
      const isEditable = d.type !== DestinationTypeEnum.Streams && (!!d.id || !!d.isNew);
      return isEditable && touchedUrlIds.has(d.localId) && !!d.url && !/^rtmps?:\/\/.+/.test(d.url);
    });

    return { hasEmptyNames, hasInvalidUrls, isValid: !hasEmptyNames && !hasInvalidUrls };
  }, [destinations, touchedUrlIds]);

  const handleSave = async () => {
    if (!roomId) return onClose();

    try {
      const deletePromises = pendingDeleteIds.map((id) => DeleteDestination(roomId, String(id)));

      const upsertPromises = destinations.flatMap((d) => {
        const original = savedDestinationsRef.current.find((o) => o.localId === d.localId);
        const isChanged = !original || !deepEqual(d, original);

        if (!isChanged) return [];

        const destinationBase = {
          name: d.name,
          url: d.url,
          streamKey: d.streamKey,
          type: d.type,
          selected: d.selected,
        };

        if (d.isNew) {
          const payload: CreateDestinationPayload = {
            destination: destinationBase,
          };
          return CreateDestination(roomId, payload);
        }

        const updatePayload: UpdateDestinationPayload = {
          destination: {
            ...destinationBase,
            id: d.id as number, // id is guaranteed if not isNew
          },
        };
        const promises: Array<Promise<unknown>> = [UpdateDestination(roomId, updatePayload)];

        // Platform-destination coalescing: the server returns BOTH a synthetic BLP
        // record (no id, backed by gr_meetings.use_blp_destination) AND a matching
        // gr_destinations row that events-api auto-seeded with the same credentials.
        // We collapse them into one card in loadDestinations, but the two server
        // records have independent `selected` flags toggled via different endpoints
        // (id-having UPDATE vs id-less PUT). When the user flips the deduped
        // platform card, send the id-less PUT too so use_blp_destination stays in
        // sync — otherwise the encoder keeps publishing via the synthetic.
        // See dedupe-destinations.ts for the dedup contract.
        const selectedChanged = !!original && original.selected !== d.selected;
        if (selectedChanged && d.type === DestinationTypeEnum.Streams) {
          const syncPayload: UpdateDestinationPayload = { destination: destinationBase };
          promises.push(UpdateDestination(roomId, syncPayload));
        }

        return promises;
      });

      await Promise.all([...deletePromises, ...upsertPromises]);
      setPendingDeleteIds([]);
      onClose();
    } catch (err) {
      console.error('Failed to save destinations:', err);
    }
  };

  return {
    destinations,
    setDestinations,
    touchedUrlIds,
    setTouchedUrlIds,
    loadDestinations,
    handleSave,
    hasChanges,
    validation,
    setPendingDeleteIds,
  };
};
